/**
 * Name That Pasta - Interactive Matching Game
 */

console.log('[Name That Pasta] Script loaded');

class NameThatPasta {
  constructor(container) {
    console.log('[Name That Pasta] Initializing game...');
    this.container = container;
    this.matches = new Map();
    this.correctMatches = 0;
    this.totalMatches = 3;
    this.score = 0;
    this.tries = 0;
    
    this.scoreElement = container.querySelector('[data-score]');
    this.triesElement = container.querySelector('[data-tries]');
    
    try {
      this.init();
      console.log('[Name That Pasta] Game initialized successfully');
    } catch (error) {
      console.error('[Name That Pasta] Error during initialization:', error);
    }
  }

  init() {
    this.shuffleNameCards();
    this.setupDragAndDrop();
    this.setupClickToMatch();
    this.setupResetButton();
    this.updateStats();
  }

  shuffleNameCards() {
    const namesSection = this.container.querySelector('.name-that-pasta__names');
    const nameCards = Array.from(namesSection.querySelectorAll('.pasta-name-card'));
    
    // Fisher-Yates shuffle algorithm
    for (let i = nameCards.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [nameCards[i], nameCards[j]] = [nameCards[j], nameCards[i]];
    }
    
    // Re-append name cards in shuffled order
    nameCards.forEach(card => {
      namesSection.appendChild(card);
    });
  }

  updateStats() {
    if (this.scoreElement) {
      this.scoreElement.textContent = this.score;
    }
    if (this.triesElement) {
      this.triesElement.textContent = this.tries;
    }
  }

  setupDragAndDrop() {
    const icons = this.container.querySelectorAll('.pasta-icon');
    const dropzones = this.container.querySelectorAll('.pasta-name-card__dropzone');

    // Make icons draggable (desktop)
    icons.forEach(icon => {
      icon.addEventListener('dragstart', (e) => {
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('text/html', icon.innerHTML);
        e.dataTransfer.setData('iconId', icon.dataset.iconId);
        icon.classList.add('dragging');
      });

      icon.addEventListener('dragend', (e) => {
        icon.classList.remove('dragging');
      });

      // Add touch support for mobile
      this.setupTouchDrag(icon, dropzones);
    });

    // Setup dropzones (desktop)
    dropzones.forEach(dropzone => {
      dropzone.addEventListener('dragover', (e) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        dropzone.classList.add('drag-over');
      });

      dropzone.addEventListener('dragleave', (e) => {
        dropzone.classList.remove('drag-over');
      });

      dropzone.addEventListener('drop', (e) => {
        e.preventDefault();
        dropzone.classList.remove('drag-over');
        
        const iconId = e.dataTransfer.getData('iconId');
        const dropzoneId = dropzone.dataset.dropzone;
        
        if (iconId && dropzoneId) {
          this.handleMatch(iconId, dropzoneId, dropzone);
        }
      });
    });
  }

  setupTouchDrag(icon, dropzones) {
    let touchStartX, touchStartY;
    let isDragging = false;
    let hasMoved = false;
    let clone = null;
    let currentDropzone = null;

    const handleTouchStart = (e) => {
      // Ignore if already matched
      if (icon.classList.contains('matched')) return;

      const touch = e.touches[0];
      touchStartX = touch.clientX;
      touchStartY = touch.clientY;
      isDragging = true;
      hasMoved = false;
    };

    const handleTouchMove = (e) => {
      if (!isDragging) return;

      const touch = e.touches[0];
      const deltaX = Math.abs(touch.clientX - touchStartX);
      const deltaY = Math.abs(touch.clientY - touchStartY);

      // Only start dragging if moved more than 5px (prevents accidental drags)
      if (deltaX > 5 || deltaY > 5) {
        hasMoved = true;
        e.preventDefault();

        // Create clone on first significant move
        if (!clone) {
          clone = icon.cloneNode(true);
          clone.classList.add('dragging-touch');
          clone.style.position = 'fixed';
          clone.style.pointerEvents = 'none';
          clone.style.zIndex = '1000';
          clone.style.opacity = '0.8';
          document.body.appendChild(clone);
          icon.style.opacity = '0.3';
        }
        
        // Move the clone
        clone.style.left = `${touch.clientX - 40}px`;
        clone.style.top = `${touch.clientY - 40}px`;

        // Check which dropzone we're over
        const elementBelow = document.elementFromPoint(touch.clientX, touch.clientY);
        const dropzoneBelow = elementBelow?.closest('.pasta-name-card__dropzone');

        // Remove drag-over class from all dropzones
        dropzones.forEach(dz => dz.classList.remove('drag-over'));

        // Add drag-over class to current dropzone
        if (dropzoneBelow) {
          currentDropzone = dropzoneBelow;
          dropzoneBelow.classList.add('drag-over');
        } else {
          currentDropzone = null;
        }
      }
    };

    const handleTouchEnd = (e) => {
      if (!isDragging) return;
      
      isDragging = false;
      icon.style.opacity = '1';

      // Remove the clone
      if (clone) {
        clone.remove();
        clone = null;
      }

      // Remove drag-over class from all dropzones
      dropzones.forEach(dz => dz.classList.remove('drag-over'));

      // Check if we ended over a dropzone (only if we actually dragged)
      if (hasMoved && currentDropzone) {
        // Prevent click event from firing
        e.preventDefault();
        
        const iconId = icon.dataset.iconId;
        const dropzoneId = currentDropzone.dataset.dropzone;
        
        if (iconId && dropzoneId) {
          this.handleMatch(iconId, dropzoneId, currentDropzone);
        }
      }

      currentDropzone = null;
      hasMoved = false;
    };

    icon.addEventListener('touchstart', handleTouchStart, { passive: true });
    icon.addEventListener('touchmove', handleTouchMove, { passive: false });
    icon.addEventListener('touchend', handleTouchEnd, { passive: false });
  }

  setupClickToMatch() {
    const icons = this.container.querySelectorAll('.pasta-icon');
    const nameCards = this.container.querySelectorAll('.pasta-name-card');
    let selectedIcon = null;

    icons.forEach(icon => {
      icon.addEventListener('click', (e) => {
        // If this icon is already matched, ignore
        if (icon.classList.contains('matched')) return;

        // Deselect any previously selected icon
        icons.forEach(i => i.classList.remove('selected'));
        
        // Select this icon
        if (selectedIcon === icon) {
          selectedIcon = null;
        } else {
          icon.classList.add('selected');
          selectedIcon = icon;
        }
      });
    });

    nameCards.forEach(nameCard => {
      nameCard.addEventListener('click', (e) => {
        // Only process if we have a selected icon
        if (!selectedIcon) return;
        
        // Don't trigger if clicking on the icon inside dropzone
        if (e.target.closest('.pasta-icon')) return;

        const dropzone = nameCard.querySelector('.pasta-name-card__dropzone');
        const iconId = selectedIcon.dataset.iconId;
        const dropzoneId = dropzone.dataset.dropzone;
        
        this.handleMatch(iconId, dropzoneId, dropzone);
        
        // Clear selection
        icons.forEach(i => i.classList.remove('selected'));
        selectedIcon = null;
      });
    });
  }

  handleMatch(iconId, dropzoneId, dropzone) {
    // Find the icon - could be in icons section or in another dropzone
    let icon = this.container.querySelector(`.name-that-pasta__icons .pasta-icon[data-icon-id="${iconId}"]`);
    let sourceDropzone = null;
    
    // If not in icons section, it must be in a dropzone
    if (!icon || icon.classList.contains('matched')) {
      // Find it in dropzones
      const allDropzones = this.container.querySelectorAll('.pasta-name-card__dropzone');
      for (const dz of allDropzones) {
        const iconInDropzone = dz.querySelector(`.pasta-icon[data-icon-id="${iconId}"]`);
        if (iconInDropzone) {
          icon = iconInDropzone;
          sourceDropzone = dz;
          break;
        }
      }
    }
    
    if (!icon) return; // Safety check
    
    // Increment tries
    this.tries++;
    this.updateStats();
    
    // If moving from one dropzone to another
    if (sourceDropzone) {
      const sourceDropzoneId = sourceDropzone.dataset.dropzone;
      
      // If the source had a correct match, decrease score
      if (this.matches.get(sourceDropzoneId) === true) {
        this.score = Math.max(0, this.score - 1);
        this.updateStats();
      }
      
      // Clear the source dropzone tracking
      this.matches.delete(sourceDropzoneId);
      sourceDropzone.classList.remove('filled', 'incorrect');
    }
    
    // Check if target dropzone already has a match
    const existingMatch = dropzone.querySelector('.pasta-icon');
    if (existingMatch && existingMatch !== icon) {
      const existingIconId = existingMatch.dataset.iconId;
      
      // If replacing a correct match in target, decrease score
      if (this.matches.get(dropzoneId) === true) {
        this.score = Math.max(0, this.score - 1);
        this.updateStats();
      }
      
      // Return the existing icon back to the icons section
      const originalIcon = this.container.querySelector(`.name-that-pasta__icons .pasta-icon[data-icon-id="${existingIconId}"]`);
      if (originalIcon) {
        originalIcon.classList.remove('matched');
      }
      
      // Remove the clone from the dropzone
      existingMatch.remove();
      
      // Remove the old match from our tracking
      this.matches.delete(dropzoneId);
      dropzone.classList.remove('filled', 'incorrect');
    }

    // Move the icon to the new dropzone
    const iconClone = icon.cloneNode(true);
    dropzone.innerHTML = '';
    dropzone.appendChild(iconClone);
    
    // Make the cloned icon draggable
    this.makeIconDraggable(iconClone);
    
    // If source was a dropzone, remove the icon from there
    if (sourceDropzone) {
      sourceDropzone.innerHTML = '';
    }
    
    // Mark the original icon in icons section as matched
    const originalIcon = this.container.querySelector(`.name-that-pasta__icons .pasta-icon[data-icon-id="${iconId}"]`);
    if (originalIcon) {
      originalIcon.classList.add('matched');
    }
    
    // Check if it's correct
    const isCorrect = iconId === dropzoneId;
    
    if (isCorrect) {
      dropzone.classList.add('filled');
      dropzone.classList.remove('incorrect');
      this.matches.set(dropzoneId, true);
      
      // Increment score for correct match
      this.score++;
      this.updateStats();
    } else {
      dropzone.classList.add('incorrect');
      dropzone.classList.remove('filled');
      this.matches.set(dropzoneId, false);
      
      // Shake animation is handled by CSS
      // Keep the incorrect state (red) visible
    }

    // Check if all matches are correct
    this.checkWinCondition();
  }

  makeIconDraggable(icon) {
    const dropzones = this.container.querySelectorAll('.pasta-name-card__dropzone');
    
    // Desktop drag and drop
    icon.setAttribute('draggable', 'true');
    
    icon.addEventListener('dragstart', (e) => {
      e.dataTransfer.effectAllowed = 'move';
      e.dataTransfer.setData('text/html', icon.innerHTML);
      e.dataTransfer.setData('iconId', icon.dataset.iconId);
      icon.classList.add('dragging');
    });

    icon.addEventListener('dragend', (e) => {
      icon.classList.remove('dragging');
    });

    // Mobile touch support
    this.setupTouchDrag(icon, dropzones);
  }

  checkWinCondition() {
    // Check if all dropzones are filled
    const allFilled = this.matches.size === this.totalMatches;
    
    if (allFilled) {
      // Check if all matches are correct
      const allCorrect = Array.from(this.matches.values()).every(val => val === true);
      
      if (allCorrect) {
        this.showSuccess();
      } else {
        this.showStats();
      }
    }
  }

  showSuccess() {
    const successSection = this.container.querySelector('.name-that-pasta__success-inline');
    const iconsSection = this.container.querySelector('.name-that-pasta__icons');
    const unifiedBar = this.container.querySelector('.name-that-pasta__unified-bar');
    
    if (successSection && iconsSection) {
      // Hide icons and show success message
      iconsSection.style.display = 'none';
      successSection.style.display = 'flex';
      
      // Show stats bar
      if (unifiedBar) {
        unifiedBar.style.display = 'flex';
      }
      
      // Add celebration class
      this.container.classList.add('game-won');
      
      // Make it rain confetti!
      this.createConfetti();
    }
  }

  createConfetti() {
    const colors = ['#FF6B6B', '#4ECDC4', '#45B7D1', '#FFA07A', '#98D8C8', '#F7DC6F', '#BB8FCE', '#85C1E2'];
    const shapes = ['square', 'circle', 'ribbon'];
    const confettiCount = 150;
    
    for (let i = 0; i < confettiCount; i++) {
      const confetti = document.createElement('div');
      confetti.className = `confetti ${shapes[Math.floor(Math.random() * shapes.length)]}`;
      
      // Random color
      confetti.style.backgroundColor = colors[Math.floor(Math.random() * colors.length)];
      
      // Random starting position across the top of the viewport
      confetti.style.left = Math.random() * 100 + 'vw';
      confetti.style.top = -10 + 'px';
      
      // Random animation duration and delay for variety
      const duration = 2 + Math.random() * 2; // 2-4 seconds
      const delay = Math.random() * 0.5; // 0-0.5 seconds delay
      confetti.style.animationDuration = duration + 's';
      confetti.style.animationDelay = delay + 's';
      
      // Add to body
      document.body.appendChild(confetti);
      
      // Remove after animation completes
      setTimeout(() => {
        confetti.remove();
      }, (duration + delay) * 1000);
    }
  }

  showStats() {
    // Just show the stats bar when game is complete but not won
    const unifiedBar = this.container.querySelector('.name-that-pasta__unified-bar');
    
    if (unifiedBar) {
      unifiedBar.style.display = 'flex';
    }
  }

  setupResetButton() {
    const resetBtns = this.container.querySelectorAll('[data-reset-game]');
    
    resetBtns.forEach(resetBtn => {
      resetBtn.addEventListener('click', () => {
        this.resetGame();
      });
    });
  }

  resetGame() {
    // Clear matches
    this.matches.clear();
    this.correctMatches = 0;
    
    // Reset score and tries
    this.score = 0;
    this.tries = 0;
    this.updateStats();

    // Remove game-won class
    this.container.classList.remove('game-won');

    // Hide success message and stats bar, show icons
    const successSection = this.container.querySelector('.name-that-pasta__success-inline');
    const iconsSection = this.container.querySelector('.name-that-pasta__icons');
    const unifiedBar = this.container.querySelector('.name-that-pasta__unified-bar');
    
    if (successSection) {
      successSection.style.display = 'none';
    }
    if (iconsSection) {
      iconsSection.style.display = 'flex';
    }
    if (unifiedBar) {
      unifiedBar.style.display = 'none';
    }

    // Clear all dropzones
    const dropzones = this.container.querySelectorAll('.pasta-name-card__dropzone');
    dropzones.forEach(dropzone => {
      dropzone.innerHTML = '';
      dropzone.classList.remove('filled', 'incorrect', 'drag-over');
    });

    // Return all icons to icons section
    const icons = this.container.querySelectorAll('.pasta-icon');
    
    // Remove matched class and return to icons section
    icons.forEach(icon => {
      icon.classList.remove('matched', 'selected', 'dragging');
      icon.style.opacity = '1'; // Reset opacity from touch drag
      if (!iconsSection.contains(icon)) {
        iconsSection.appendChild(icon);
      }
    });

    // Re-initialize click-to-match
    this.setupClickToMatch();
    
    // Shuffle name cards for new game
    this.shuffleNameCards();
  }
}

// Initialize on page load
document.addEventListener('DOMContentLoaded', () => {
  console.log('[Name That Pasta] DOM Content Loaded');
  const sections = document.querySelectorAll('[data-section-type="name-that-pasta"]');
  console.log('[Name That Pasta] Found', sections.length, 'section(s)');
  
  sections.forEach((section, index) => {
    try {
      console.log(`[Name That Pasta] Initializing section ${index + 1}/${sections.length}`);
      new NameThatPasta(section);
    } catch (error) {
      console.error(`[Name That Pasta] Error initializing section ${index + 1}:`, error);
    }
  });
});

// Support for Shopify theme editor
if (window.Shopify && window.Shopify.designMode) {
  document.addEventListener('shopify:section:load', (event) => {
    const section = event.target.querySelector('[data-section-type="name-that-pasta"]');
    if (section) {
      new NameThatPasta(section);
    }
  });
}

